/*
This file is located in the main branch
Last time the file was updated (UTC): [11.12.2024 08:50:34]
Permissions to update outside the main branch: [false]
*/

import {
    AccordionBody,
    Button,
    Draggable,
    requiredRule,
    ValidatedDynamicList,
    ValidatedDynamicListFormComponentProps,
    ValidatedInput,
} from '@finbeat/react-toolkit';
import { faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import BaseReactHookFormValue from 'base-components/BaseReactHookForm/BaseReactHookFormValue';
import { t } from 'i18next';
import { QuestionaryBlockItem, QuestionaryInfo } from 'models/api/questionary';
import { useQuestionaryItemListState } from 'providers/QuestionaryItemListProvider';
import { useCallback, useMemo, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { Accordion, AccordionHeader, AccordionItem, Col, Row } from 'reactstrap';
import {
    QuestionariesBlocksItemForm,
    QuestionariesBlocksItemFormWrapper,
} from './QuestionariesBlocksItemForm';
import './QuestionaryModal.scss';

const emptyBlock = {
    id: 0,
    name: '',
    blockId: 0,
    dsc: '',
    items: [],
};

const QuestionaryModalQuestionariesBlocks = ({ pageIdx }: { pageIdx: number }) => {
    const [open, setOpen] = useState('');

    const { setFocus } = useFormContext<QuestionaryInfo>();

    const { fields, append, swap, remove } = useFieldArray<QuestionaryInfo>({
        name: `pages.${pageIdx}.blocks`,
    });

    const addBlock = () => {
        append(emptyBlock);
        setTimeout(() => setFocus(`pages.${pageIdx}.blocks.${fields.length}.name`), 1);
    };

    const reorderBlocks = (source: number, target: number) => {
        swap(source, target);
    };

    const deleteBlock = (idx: number) => {
        remove(idx);
    };

    const toggle = (id: string) => setOpen((prev) => (prev === id ? '' : id));

    const accordionProps = {
        open: open,
        toggle: toggle,
    };

    return (
        <>
            <Accordion {...accordionProps}>
                {fields.map(({ id }, idx) => (
                    <Draggable<number>
                        key={id}
                        typeKey="BLOCKS"
                        sourceKey={idx}
                        onItemReorder={reorderBlocks}
                    >
                        <AccordionItem>
                            <AccordionHeader tag="div" targetId={id}>
                                <div>
                                    <BaseReactHookFormValue<QuestionaryInfo>
                                        name={`pages.${pageIdx}.blocks.${idx}.name`}
                                    />
                                    {' - '}
                                    <BaseReactHookFormValue<QuestionaryInfo>
                                        name={`pages.${pageIdx}.blocks.${idx}.dsc`}
                                    />
                                    <FontAwesomeIcon
                                        icon={faTrashCan}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            deleteBlock(idx);
                                        }}
                                        className="text-danger"
                                    />
                                </div>
                            </AccordionHeader>
                            <AccordionBody
                                accordionId={id}
                                toggle={(val) => val && setOpen(val.toString())}
                                className={`questionary-modal__content`}
                            >
                                <Row>
                                    <Col>
                                        <ValidatedInput
                                            name={`pages.${pageIdx}.blocks.${idx}.name`}
                                            label={t('BackOfficeName')}
                                            rules={{ required: requiredRule }}
                                        />
                                    </Col>
                                    <Col>
                                        <ValidatedInput
                                            name={`pages.${pageIdx}.blocks.${idx}.dsc`}
                                            label={t('BackOfficeDescription')}
                                            rules={{ required: requiredRule }}
                                        />
                                    </Col>
                                </Row>
                                <Item pageIdx={pageIdx} blockIdx={idx} />
                            </AccordionBody>
                        </AccordionItem>
                    </Draggable>
                ))}
            </Accordion>

            <div className="text-right">
                <Button onClick={() => addBlock()}>
                    <Trans i18nKey="BackOfficeAddBlock" />
                </Button>
            </div>
        </>
    );
};

const Item = ({ pageIdx, blockIdx }: { pageIdx: number; blockIdx: number }) => {
    const items = useQuestionaryItemListState();
    const FormComponent = useCallback(
        (props: ValidatedDynamicListFormComponentProps) => (
            <QuestionariesBlocksItemForm {...props} pageIdx={pageIdx} blockIdx={blockIdx} />
        ),
        []
    );

    const emptyRecord = useMemo(
        () => ({
            id: 0,
            itemId: parseInt(items[0].value),
            flgRequired: false,
        }),
        [items]
    );

    return (
        <ValidatedDynamicList<QuestionaryInfo, QuestionaryBlockItem>
            formComponent={FormComponent}
            wrapper={QuestionariesBlocksItemFormWrapper}
            emptyRecord={emptyRecord}
            name={`pages.${pageIdx}.blocks.${blockIdx}.items`}
            compareFunc={(a, b) => a?.itemId === b?.itemId}
        />
    );
};

export default QuestionaryModalQuestionariesBlocks;
